import { Capacitor } from '@capacitor/core';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';

/**
 * Almacén del token de sesión (JWT).
 *
 * En nativo el token vive en el Keychain (iOS) / Keystore (Android) cifrado, no en
 * localStorage en claro — importante al tratarse de datos de salud. Para no tener
 * que convertir en asíncronos los cientos de accesos síncronos al token repartidos
 * por la app, se mantiene una CACHÉ EN MEMORIA: getToken() es síncrono y lee la
 * caché; las escrituras actualizan la caché al instante y persisten en segundo
 * plano. La caché se hidrata al arrancar con initTokenStore(), que AuthContext
 * espera antes de dar la sesión por restaurada.
 *
 * En web no hay Keychain, así que se usa localStorage (comportamiento previo).
 */

const native = Capacitor.isNativePlatform();
const TOKEN_KEY = 'token';
const ADMIN_TOKEN_KEY = 'adminToken';

const cache: Record<string, string | null> = {
  [TOKEN_KEY]: null,
  [ADMIN_TOKEN_KEY]: null,
};
let hydrated = false;

async function readPersisted(key: string): Promise<string | null> {
  if (native) {
    try {
      return await SecureStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return localStorage.getItem(key);
}

function writePersisted(key: string, value: string): void {
  if (native) {
    // Fire-and-forget: la caché ya está actualizada; el Keychain se escribe detrás.
    SecureStorage.setItem(key, value).catch(() => {});
  } else {
    localStorage.setItem(key, value);
  }
}

function removePersisted(key: string): void {
  if (native) {
    SecureStorage.removeItem(key).catch(() => {});
  } else {
    localStorage.removeItem(key);
  }
}

// Hidrata la caché desde el almacén persistente. Idempotente. Debe llamarse (y
// esperarse) antes de leer el token al arrancar en nativo.
export async function initTokenStore(): Promise<void> {
  if (hydrated) return;
  cache[TOKEN_KEY] = await readPersisted(TOKEN_KEY);
  cache[ADMIN_TOKEN_KEY] = await readPersisted(ADMIN_TOKEN_KEY);

  // Migración: en nativo, si un token quedó en localStorage de una versión previa
  // (cuando no se usaba Keychain), moverlo al almacén seguro y borrarlo de claro.
  // Así los usuarios ya logueados no tienen que volver a iniciar sesión.
  if (native) {
    for (const key of [TOKEN_KEY, ADMIN_TOKEN_KEY]) {
      const legacy = localStorage.getItem(key);
      if (legacy) {
        if (!cache[key]) {
          cache[key] = legacy;
          writePersisted(key, legacy);
        }
        localStorage.removeItem(key);
      }
    }
  }

  hydrated = true;
}

export const getToken = (): string | null => cache[TOKEN_KEY];
export const setToken = (token: string): void => {
  cache[TOKEN_KEY] = token;
  writePersisted(TOKEN_KEY, token);
};
export const clearToken = (): void => {
  cache[TOKEN_KEY] = null;
  removePersisted(TOKEN_KEY);
};

// Token del admin durante una impersonación (también un JWT, también seguro).
export const getAdminToken = (): string | null => cache[ADMIN_TOKEN_KEY];
export const setAdminToken = (token: string): void => {
  cache[ADMIN_TOKEN_KEY] = token;
  writePersisted(ADMIN_TOKEN_KEY, token);
};
export const clearAdminToken = (): void => {
  cache[ADMIN_TOKEN_KEY] = null;
  removePersisted(ADMIN_TOKEN_KEY);
};
